import React, { useMemo, useCallback, useState, useContext } from 'react';

import { Icon, Dropdown, Menu, Tooltip, Popconfirm, Popover, Input } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { ListView, PublishList } from './styles';
import { VERSION_CONTEXT_KEYS, useVersionContext } from '../../../context/complexVersionContext';
import { TEMPLATE_ENVIRONMENT_PAGE } from '../../../artboard/PublishOverlay/translationValues';
import {
  DraftIcon,
  NotYetTestedIcon,
  PublishVersionIcon,
  testingIcon,
} from '../../../images/svgIcons';
import { TagSettings } from './TagSettings';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { getRejectedCommentsForDocuments } from '../../../utils/services/complexDocumentsAPI';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useComplexDocumentHistory,
} from '../../../context/complexDocumentContext';
import { applyTag, getTagDocument } from '../../../utils/services/complexVersionsAPI';
import {
  addUuidToAllBlocksInDocument,
  replaceEmailTemplate,
  removeCurrentBusinessLogics,
} from '../../../utils/masterTemplateHelper';
import { BusinessLogic } from '../../../context/businessLogicContext';
import { handleBusinessLogic } from '../complexDocumentHelper';
import { COMPLEX_DOCUMENT_VERSION, DESIGN_TYPE } from '../../../utils/complexDocumentConstants';
import { getTimeStampDetails } from '../../../utils/helper';
import { COMPLEX_EMAIL_CONTEXT_KEYS, useEmailContext } from '../../../context/complexEmailContext';
import { updateTemplateInsert } from '../../../utils/services/insertsApi';
import { deleteAttachment, patchAttachments, patchEmailSubject } from '../../../utils/services/complexEmailAPI';

const { TextArea } = Input;
const NAMESPACES = ['TEMPLATE_ENVIRONMENT_PAGE'];
const qaPublishedEnv = [COMPLEX_DOCUMENT_VERSION.QA_READY, COMPLEX_DOCUMENT_VERSION.PUBLISHED];
export const VersionSettings = props => {
  const { lifeCycle, currentVisibleVersion, updateVersionStore } = props;
  const [isVisible, setIsVisible] = useState(false);
  const [rejectedBy, setRejectedBy] = useState('');
  const [rejectionComments, setRejectionComments] = useState('');
  const { businessLogics, setBusinessLogics } = useContext(BusinessLogic);
  const versionsSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.TAGS_LIST], []);
  const [tags] = useVersionContext(versionsSelector, `VersionSettings`);

  const [visibleKey, setVisibleKey] = useState(null);

  const draftEditableSelector = useCallback(
    store => ({
      isDraftEditable: store[CONTEXT_KEYS.IS_DRAFT_EDITABLE],
      currentDesign: store[CONTEXT_KEYS.CURRENT_DESIGN],
    }),
    [],
  );
  const [{ isDraftEditable, currentDesign }, updateArtboard, , , storeGet] = useArtboardContext(
    draftEditableSelector,
    `ApprovalProcess`,
  );

  const customDocumentSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE],
    [],
  );
  const [isEmail, methods] = useDocumentContext(customDocumentSelector, 'VersionSettings');

  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(customSelector, `VersionSettings`);

  const emailSelector = useCallback(() => null, []);
  const [, emailStore] = useEmailContext(emailSelector, 'VersionSettings');

  const [, historyStore] = useComplexDocumentHistory(store => store.history);
      
  const { t } = useTranslation(NAMESPACES);

  const handleChange = useCallback(async type => {
    updateVersionStore({
      [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: type,
      [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG]: {},
    });
  }, []);

  const handleReplaceDesign = useCallback(
    async tagId => {
      if (!tagId) return;
      if (currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.DRAFT) {
        updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: true });
        try {
          const response = await getTagDocument(tagId);
          if (response?.statusCode === 5709 && response?.data) {
            const tagDocument = response?.data;
            if (currentDesign === DESIGN_TYPE.DOCUMENT && !isEmail) {
              const flows = addUuidToAllBlocksInDocument(tagDocument.flows);
              const callback = ({ blockIdsMap, flowIdsMap }) => {
                const autosaveCallback = async () => {
                  // delete all business logics and replace it with business logics from mydesign template
                  removeCurrentBusinessLogics(businessLogics, setBusinessLogics);
                  handleBusinessLogic({
                    methods,
                    selectedTemplate: tagDocument,
                    setBusinessLogics,
                    blockIdsMap,
                    flowIdsMap,
                  });
                  const previousInserts = methods.get()[
                    [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]
                  ];
                  if (previousInserts && previousInserts.length) {
                    const payload = previousInserts.map(({ id, relationId, orderNumber }) => ({
                      id,
                      relationId,
                      orderNumber,
                      isDeleted: true,
                    }));
                    await updateTemplateInsert(tagDocument.id, payload);
                    methods.set({
                      [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: [],
                    });
                  }
                  if (tagDocument.inserts && tagDocument.inserts.length) {
                    const payload = tagDocument.inserts.map(({ id, orderNumber }) => ({
                      id,
                      orderNumber,
                    }));
                    const response = await updateTemplateInsert(tagDocument.id, payload);
                    if (response?.statusCode === 133 && response.data) {
                      const updatedTaggedInserts = tagDocument.inserts.map(prevItem => {
                        const match = response.data.find(
                          r => r.id === prevItem.id && r.orderNumber === prevItem.orderNumber,
                        );
                        return match ? { ...prevItem, relationId: match.relationId } : prevItem;
                      });
                      methods.set({
                        [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: updatedTaggedInserts,
                      });
                    }
                  }
                };
                methods.set({
                  [COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: autosaveCallback,
                  [COMPLEX_DOCUMENT_CONTEXT_KEYS.NEW_LANGUAGE_CODE]: tagDocument?.language,
                  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION]: true,
                });
              };
              methods.replaceDocument(flows, tagDocument?.style, callback, tagDocument?.summary);
            } else if (currentDesign === DESIGN_TYPE.DOCUMENT && isEmail) {
              if (currentDesign === 'document') {
                const autosaveCallback = () => {
                  const { subject = {}, attachments } = tagDocument;
                  const templateId = methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
                  const replaceSubject = async subject => {
                    subject.blockId = undefined;
                    const res = await patchEmailSubject(
                      methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
                      subject,
                    );
                    if (res.statusCode === 3109) {
                      emailStore.set({
                        [COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT]: Object.assign({}, subject, {
                          blockId: subject.blockId === undefined ? res.data.id : subject.blockId,
                        }),
                      });
                    }
                  };
                  const replaceAttachments = async attachments => {
                    const existingAttachments = emailStore.get()[
                      COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS
                    ];
                    if (existingAttachments?.length > 0) {
                      const promises = [];
                      for (let i = 0; i < existingAttachments.length; i++) {
                        promises.push(await deleteAttachment(existingAttachments[i].attachmentId));
                      }
                      const res = await Promise.allSettled(promises);
                      if (Array.isArray(res) && res.every(r => r.value.statusCode === 1304)) {
                        emailStore.set({ [COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]: [] });
                      }
                    }
                    if (attachments?.length > 0) {
                      for (let i = 0; i < attachments.length; i++) {
                        const attachmentObj = attachments[i];
                        let response;
                        if (attachmentObj.contentType === 'document') {
                          response = await patchAttachments(templateId, {
                            contentType: 'document',
                            documentId: attachmentObj.documentId,
                            name: attachmentObj.documentName,
                            passwordProtected: attachmentObj?.passwordProtected || false,
                            isEditable: attachmentObj?.isEditable || false,
                          });
                        } else {
                          response = await patchAttachments(templateId, {
                            contentType: 'application/pdf',
                            storeId: attachmentObj.storeId,
                            name: attachmentObj.documentName,
                          });
                        }
                        if (response?.statusCode === 1302) {
                          const newAttachment = {
                            attachmentId: response.id,
                            contentType: attachmentObj.contentType,
                            documentName: attachmentObj.documentName,
                            thumbnail: attachmentObj?.thumbnail,
                            passwordProtected: attachmentObj?.passwordProtected,
                          };
                          emailStore.set({
                            [COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS]: [
                              ...(emailStore.get()[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS] || []),
                              newAttachment,
                            ],
                            [response.id]: Object.assign(
                              {},
                              newAttachment,
                              newAttachment.contentType === 'document'
                                ? {
                                    documentId: attachmentObj.documentId,
                                    variableContract: attachmentObj?.variableContract,
                                    flows: attachmentObj?.flows,
                                  }
                                : {
                                    storeId: attachmentObj.storeId,
                                  },
                            ),
                          });
                        }
                      }
                    }
                  };
                  replaceSubject(subject);
                  replaceAttachments(attachments);
                };
                methods.set({
                  [COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: autosaveCallback,
                });
                replaceEmailTemplate(
                  { element: tagDocument },
                  methods,
                  businessLogics,
                  setBusinessLogics,
                );
              } else if (currentDesign === 'block') {
                methods.setPage('replaceBlocks', {
                  uuid: methods.get()['flows'][0],
                  blocks: tagDocument?.flow?.blocks || tagDocument?.blocks,
                  style: methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES],
                  flowStyle: tagDocument?.flow?.style || tagDocument?.style,
                  blocksIdsMap: new Map(),
                });
              }
            } else if (currentDesign === 'flow' || currentDesign === 'block') {
              methods.setPage('replaceBlocks', {
                uuid: methods.get()['flows'][0],
                blocks: tagDocument?.flow?.blocks || tagDocument?.blocks,
                style: methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES],
                flowStyle: tagDocument?.flow?.style || tagDocument?.style,
                blocksIdsMap: new Map(),
              });
            }
            getTimeStampDetails(documentId, updateVersionStore, currentDesign, isEmail);
          }
        } catch (e) {
          console.log(e, 'An error occured while applying draft tag');
        } finally {
          updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
        }
      } else if (qaPublishedEnv.includes(currentVisibleVersion)) {
        const response = await applyTag(
          currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY
            ? COMPLEX_DOCUMENT_VERSION.SANDBOX
            : COMPLEX_DOCUMENT_VERSION.LIVE,
          tagId,
        );
        if (response?.statusCode === 5706) {
          updateVersionStore({ [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: '' });
          getTimeStampDetails(documentId, updateVersionStore, currentDesign, isEmail);
          const timeout = setTimeout(() => {
            updateVersionStore({
              [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: currentVisibleVersion,
            });
            clearTimeout(timeout);
          }, 300);
        }
      }
    },
    [currentVisibleVersion, currentDesign, businessLogics, isEmail, documentId],
  );

  const handleConfirm = tagId => {
    handleReplaceDesign(tagId);
    setVisibleKey(null); // close popconfirm
  };

  const applicableTags = useMemo(() => {
    if (currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.DRAFT) {
      return tags.filter(
        tag => tag.env === COMPLEX_DOCUMENT_VERSION.DRAFT || tag.canApplyDesignToDraft,
      );
    }
    if (currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY) {
      return tags.filter(
        tag =>
          tag.env === COMPLEX_DOCUMENT_VERSION.SANDBOX || tag.env === COMPLEX_DOCUMENT_VERSION.LIVE,
      );
    }
    if (currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.PUBLISHED) {
      return tags.filter(tag => tag.env === COMPLEX_DOCUMENT_VERSION.LIVE);
    }
    return [];
  }, [currentVisibleVersion, tags]);

  const versionMap = {
    draft: t(
      TEMPLATE_ENVIRONMENT_PAGE.VERSION_DRAFT.key,
      TEMPLATE_ENVIRONMENT_PAGE.VERSION_DRAFT.value,
    ),
    sandbox: t(
      TEMPLATE_ENVIRONMENT_PAGE.VERSION_TESTING.key,
      TEMPLATE_ENVIRONMENT_PAGE.VERSION_TESTING.value,
    ),
    live: t(
      TEMPLATE_ENVIRONMENT_PAGE.VERSION_PUBLISHED.key,
      TEMPLATE_ENVIRONMENT_PAGE.VERSION_PUBLISHED.value,
    ),
  };

  const containerStyle = {
    width: '100%',
    flex: '0 0 auto',
  };

  const titleMap = {
    [COMPLEX_DOCUMENT_VERSION.QA_READY]: TEMPLATE_ENVIRONMENT_PAGE.REPLACE_DESIGN_QA,
    [COMPLEX_DOCUMENT_VERSION.PUBLISHED]: TEMPLATE_ENVIRONMENT_PAGE.REPLACE_DESIGN_PUBLISHED,
  };

  const titleKey = titleMap[currentVisibleVersion] || TEMPLATE_ENVIRONMENT_PAGE.REPLACE_DESIGN_QUESTIONNAIRE;

  const menu = (
    <Menu>
      {applicableTags.map(item => (
        <Menu.Item key={item.tagId} style={{ cursor: 'default' }}>
          <Popconfirm
            title={t(titleKey.key, titleKey.value)}
            onConfirm={() => handleConfirm(item.tagId)}
            onCancel={() => setVisibleKey(null)}
            visible={visibleKey === item.tagId}
            onVisibleChange={visible => setVisibleKey(visible ? item.tagId : null)}
            okText={t(TEMPLATE_ENVIRONMENT_PAGE.OK_LABEL.key, TEMPLATE_ENVIRONMENT_PAGE.OK_LABEL.value)}
            cancelText={t(TEMPLATE_ENVIRONMENT_PAGE.CANCEL_LABEL.key, TEMPLATE_ENVIRONMENT_PAGE.CANCEL_LABEL.value)}
            placement="right"
            overlayStyle={{width: 350}}
          >
            <span
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {item.tagName} - {versionMap[item.env]}
            </span>
          </Popconfirm>
        </Menu.Item>
      ))}
    </Menu>
  );

  const getRejectComments = async () => {
    try {
      let templateType = currentDesign;
      if (isEmail) {
        templateType =
          currentDesign === DESIGN_TYPE.DOCUMENT ? 'email' : DESIGN_TYPE.BLOCK ? 'email_block' : '';
      }
      const response = await getRejectedCommentsForDocuments(documentId, templateType);
      if ([5607].includes(response?.statusCode)) {
        setIsVisible(true);
        setRejectedBy(response.data?.rejectedBy);
        setRejectionComments(response.data?.rejectedComments);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleVisibleChange = visibilityState => {
    setIsVisible(visibilityState);
  };

  const renderList = (type, label, icon) => {
    const noPublish = type === COMPLEX_DOCUMENT_VERSION.PUBLISHED && !lifeCycle[type];
    const noTesting = type === COMPLEX_DOCUMENT_VERSION.QA_READY && !lifeCycle[type];
    const disabled = noPublish || noTesting;
    const isRejected =
      currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY &&
      type === COMPLEX_DOCUMENT_VERSION.QA_READY &&
      lifeCycle[COMPLEX_DOCUMENT_VERSION.REJECTED];
    return (
      <div style={{ cursor: disabled && 'not-allowed' }}>
        <ListView
          activeVersion={currentVisibleVersion === type}
          isRejected={isRejected}
          onClick={() => handleChange(type)}
          disabled={disabled}
        >
          <div className="env-name">
            <Icon component={icon} />
            <div className="column">
              <span>{label}</span>
              <span className={currentVisibleVersion === type ? 'note-text active' : 'note-text'}>
                {lifeCycle[type] ? moment(lifeCycle[type]).format('lll') : ''}
                {noPublish &&
                  t(
                    TEMPLATE_ENVIRONMENT_PAGE.NOT_YET_PUBLISHED_LABEL.key,
                    TEMPLATE_ENVIRONMENT_PAGE.NOT_YET_PUBLISHED_LABEL.value,
                  )}
                {noTesting &&
                  t(
                    TEMPLATE_ENVIRONMENT_PAGE.TIME_NOT_AVAILABLE_LABEL.key,
                    TEMPLATE_ENVIRONMENT_PAGE.TIME_NOT_AVAILABLE_LABEL.value,
                  )}
              </span>
            </div>
          </div>
          <div>
            {currentVisibleVersion === type && (
              <div>
                <div className="current-version">
                  <Icon type="check" />
                  {isDraftEditable && applicableTags.length > 0 && (
                    <Dropdown
                      overlay={menu}
                      trigger={['click']}
                      onVisibleChange={visible => !visible && setVisibleKey(null)}
                    >
                      <Tooltip
                        title={t(
                          TEMPLATE_ENVIRONMENT_PAGE.REPLACE_DESIGN_TOOLTIP.key,
                          TEMPLATE_ENVIRONMENT_PAGE.REPLACE_DESIGN_TOOLTIP.value,
                        )}
                      >
                        <Icon type="retweet" />
                      </Tooltip>
                    </Dropdown>
                  )}
                </div>
                {isRejected && (
                  <button
                    onClick={() => {
                      getRejectComments();
                    }}
                    className="comment-button"
                  >
                    {t(
                      TEMPLATE_ENVIRONMENT_PAGE.VIEW_COMMENT_BUTTON.key,
                      TEMPLATE_ENVIRONMENT_PAGE.VIEW_COMMENT_BUTTON.value,
                    )}
                    <Popover
                      style={{
                        width: '75%',
                        borderRadius: '16px',
                        outline: '1px solid grey',
                        padding: '10px',
                      }}
                      content={customContent()}
                      onVisibleChange={handleVisibleChange}
                      visible={isVisible}
                      trigger="click"
                      placement="right"
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        </ListView>
      </div>
    );
  };
  const qareadystatus = lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE];

  const customContent = () => {
    return (
      <div>
        <p>
          {t(
            TEMPLATE_ENVIRONMENT_PAGE.COMMENTED_BY.key,
            TEMPLATE_ENVIRONMENT_PAGE.COMMENTED_BY.value,
          )} {rejectedBy}
          </p>
        <TextArea
          value={rejectionComments}
          readOnly
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{
            background: '#fff',
            color: '#000',
            borderRadius: 2,
            resize: 'none',
            marginTop: '8px',
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div style={containerStyle}>
        <PublishList>
          {renderList(
            COMPLEX_DOCUMENT_VERSION.PUBLISHED,
            t(
              TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_ENV_TAB.key,
              TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_ENV_TAB.value,
            ),
            PublishVersionIcon,
          )}
          {renderList(
            COMPLEX_DOCUMENT_VERSION.QA_READY,
            t(
              TEMPLATE_ENVIRONMENT_PAGE.TESTING_ENV_TAB.key,
              TEMPLATE_ENVIRONMENT_PAGE.TESTING_ENV_TAB.value,
            ),
            qareadystatus ? testingIcon : NotYetTestedIcon,
          )}
          {renderList(
            COMPLEX_DOCUMENT_VERSION.DRAFT,
            t(
              TEMPLATE_ENVIRONMENT_PAGE.DRAFT_ENV_TAB.key,
              TEMPLATE_ENVIRONMENT_PAGE.DRAFT_ENV_TAB.value,
            ),
            DraftIcon,
          )}
        </PublishList>
      </div>
      <TagSettings isDraftEditable={isDraftEditable} />
    </>
  );
};
